// Category covers and hero art are hosted on Pexels/Unsplash, whose originals
// run 4000-7000px wide. They were being served at full size into slots ~360px
// across, costing several MB per page on mobile. Both hosts resize on demand,
// so ask them for something close to the rendered size.

const WIDTHS = [400, 800, 1200, 1600];

export function sizedImage(url, width = 800) {
  if (!url || typeof url !== "string") return url;
  try {
    const u = new URL(url);
    if (u.hostname.endsWith("pexels.com")) {
      u.searchParams.set("auto", "compress");
      u.searchParams.set("cs", "tinysrgb");
      u.searchParams.set("w", String(width));
      return u.toString();
    }
    if (u.hostname.endsWith("unsplash.com")) {
      u.searchParams.set("w", String(width));
      u.searchParams.set("q", "80");
      u.searchParams.set("fm", "jpg");
      return u.toString();
    }
    return url; // unknown host — leave it alone rather than build a broken URL
  } catch {
    return url;
  }
}

export function imageSrcSet(url, widths = WIDTHS) {
  if (!url || typeof url !== "string") return undefined;
  try {
    const u = new URL(url);
    if (!/(pexels|unsplash)\.com$/.test(u.hostname)) return undefined;
  } catch {
    return undefined;
  }
  return widths.map((w) => `${sizedImage(url, w)} ${w}w`).join(", ");
}

// Every cover on the site is an <img class="w-full h-full object-cover"> inside
// a box whose shape is declared in CSS (`aspect-[16/9]`, `aspect-[3/4]`, ...).
// That styling still decides the rendered size, but on its own it leaves the
// <img> with no intrinsic dimensions, so the browser cannot reserve the space
// until the bytes arrive and the page reflows around every image as it loads.
//
// These attributes exist to state the ratio, not the display size: CSS
// overrides both numbers. So the height is derived from the SLOT's ratio (what
// the reader will actually see, after object-cover has cropped) rather than
// from the source file, and the width is the width we ask the CDN for.
//
// `ratio` is written the same way as the Tailwind class on the wrapper —
// keep the two in step, they describe the same box.
export function aspectSize(ratio, width = 800) {
  const [w, h] = String(ratio || "").split("/").map(Number);
  if (!w || !h || !Number.isFinite(w) || !Number.isFinite(h)) return {};
  return { width, height: Math.round((width * h) / w) };
}

/**
 * Props to spread onto an <img> for a slot that is `sizes` wide in CSS terms
 * and `ratio` shaped (e.g. "3/4", matching the wrapper's aspect-[3/4]).
 */
export function responsiveImage(url, { width = 800, sizes, ratio } = {}) {
  return {
    src: sizedImage(url, width),
    srcSet: imageSrcSet(url),
    sizes,
    ...aspectSize(ratio, width),
  };
}
